// src/components/billing/PlanCard.jsx
import React, { useState, useEffect } from 'react';
import {
  Button, Typography, Box, CircularProgress, Chip, TextField, Stack, Skeleton,
  Tooltip, useTheme, alpha, List, ListItem, ListItemIcon, ListItemText, Divider, IconButton, Collapse
} from '@mui/material';
import {
  ShoppingCartCheckoutRounded as ShoppingCartIcon,
  CheckCircleRounded as CheckCircleIcon,
  SettingsApplicationsRounded as ManageIcon,
  WorkspacePremiumRounded as PremiumIcon,
  LocalOfferRounded as TagIcon,
  AutorenewRounded as AutorenewIcon,
  ArrowForwardRounded as ArrowRightIcon,
  DoneAllRounded as DoneAllIcon,
  ExpandMoreRounded as ExpandMoreIcon,
  InfoOutlined as InfoIcon,
  MonetizationOnRounded as CreditsIcon
} from '@mui/icons-material';
import SectionCard from './SectionCard'; // Ensure this path is correct

const PlanCard = ({
  plan,
  onSubscribe,
  isLoading,
  isCurrentPlan,
  isFreeTierActive, // Note: isFreeTierActive usually means it's also the current plan
  onActivateFreeTier,
  isEligibleForFreeTier,
  onManageInPortal,
  delay = 0,
}) => {
  const theme = useTheme();

  const isSubscription = plan?.product_type === 'subscription';
  const isOneTimePurchase = plan?.product_type === 'one_time_purchase';
  
  const initialQuantity = isOneTimePurchase ? (plan?.min_quantity || 1) : 1;
  const [quantity, setQuantity] = useState(initialQuantity);
  
  const [totalPrice, setTotalPrice] = useState(0);
  const [expandedFeatures, setExpandedFeatures] = useState(false);

  const isDesignatedFreeTier = plan?.is_designated_free_tier === true || (!plan?.is_designated_free_tier && plan?.tier_name === "Free Monthly Trial");
  const stripePriceId = plan?.stripe_price_id;

  useEffect(() => {
    const newInitialQuantity = isOneTimePurchase ? (plan?.min_quantity || 1) : 1;
    setQuantity(newInitialQuantity);
  }, [plan, isOneTimePurchase]);

  useEffect(() => {
    const priceNum = parseFloat(plan?.price_usd_decimal); // Parse to number
    if (isNaN(priceNum)) {
      setTotalPrice(0);
      return;
    }
    if (isOneTimePurchase) {
      setTotalPrice(quantity * priceNum);
    } else if (isSubscription) {
      setTotalPrice(priceNum);
    }
  }, [quantity, plan?.price_usd_decimal, isOneTimePurchase, isSubscription, plan]);

  const handleQuantityChange = (event) => {
    let val = parseInt(event.target.value, 10);
    const minQty = plan?.min_quantity || 1;
    const maxQty = plan?.max_quantity || 10000;
    if (isNaN(val) || val < minQty) val = minQty;
    if (val > maxQty) val = maxQty;
    setQuantity(val);
  };

  const handleToggleFeatures = () => setExpandedFeatures(!expandedFeatures);

  const renderPriceSection = () => {
    // totalPrice is already a number from the useEffect calculation
    const displayTotalPrice = totalPrice;

    return (
      <Box sx={{ textAlign: 'center', my: 1, py: 2, borderRadius: '16px', background: alpha(theme.palette.grey[isDesignatedFreeTier && isEligibleForFreeTier ? 300 : 100], 0.4) }}>
        {displayTotalPrice !== null && !isNaN(displayTotalPrice) ? (
          <>
            <Typography variant="h2" component="p" sx={{ fontWeight: 700, color: isDesignatedFreeTier && isEligibleForFreeTier ? theme.palette.success.dark : theme.palette.primary.dark, lineHeight: 1, letterSpacing: '-1.5px' }}>
              {(displayTotalPrice === 0 && isSubscription) ? "Free" : `$${displayTotalPrice.toFixed(2)}`}
            </Typography>
            <Typography variant="overline" component="p" color="text.secondary" sx={{ lineHeight: 1.2, mt:0.5 }}>
              {isSubscription ? 'Per Month' : (isOneTimePurchase && quantity > 0 && plan?.credits_to_add ? `For ${plan.credits_to_add * quantity} Credits` : (plan?.price_usd_decimal ? 'Per Credit' : ''))}
            </Typography>
          </>
        ) : (
          <Typography variant="h5" color="text.disabled" sx={{my:2, fontWeight:500}}>Price Info Unavailable</Typography>
        )}
      </Box>
    );
  };

  const renderCreditsInfo = () => {
    const creditsText = isSubscription 
      ? `${plan.credits_per_month || 0} Credits / Month`
      : `${(plan.credits_to_add || 0) * quantity} Credits Total`;
    
    const showCredits = (isSubscription && plan?.credits_per_month !== null && plan?.credits_per_month !== undefined) || 
                        (isOneTimePurchase && plan?.credits_to_add !== null && plan?.credits_to_add !== undefined && quantity > 0);

    if (!showCredits) return null;

    return (
        <Chip 
            icon={<CreditsIcon />}
            label={creditsText} 
            variant="filled"
            size="medium"
            sx={{ 
                mt: 1, mb: 2, fontWeight: 500, borderRadius: '10px',
                bgcolor: alpha(theme.palette.info.main, 0.9), 
                color: theme.palette.info.contrastText,
                py: 2, px: 1.5, fontSize: '0.9rem',
                width:'100%',
                justifyContent:'center'
            }} 
        />
    );
  }
  
  const renderFeaturesList = () => {
    const features = Array.isArray(plan?.features) ? plan.features : (plan?.description && !Array.isArray(plan.features) ? [plan.description] : []);
    const defaultText = "Key benefits and features of this plan.";
    const displayFeatures = features.length > 0 ? features : [defaultText];
    const featureLimit = 2; 

    if (displayFeatures.length === 0 && !plan?.description) return null;

    return (
      <>
        <Divider sx={{my: 2, display: displayFeatures.length > 0 ? 'block' : 'none'}}>
            <Chip label="Plan Highlights" size="small" variant="outlined"/>
        </Divider>
        <List dense sx={{mb:1, flexGrow:1, px:0.5, minHeight: displayFeatures.length > 0 ? 70 : 'auto' }}>
            {displayFeatures.slice(0, expandedFeatures ? displayFeatures.length : featureLimit).map((feature, index) => (
                <ListItem key={index} disableGutters sx={{py:0.35, alignItems:'flex-start'}}>
                    <ListItemIcon sx={{minWidth: 28, mt:0.4}}><DoneAllIcon fontSize="small" color="success"/></ListItemIcon>
                    <ListItemText primary={feature} primaryTypographyProps={{variant:'body2', color: theme.palette.text.primary, lineHeight:1.4}}/>
                </ListItem>
            ))}
        </List>
        {displayFeatures.length > featureLimit && (
            <Button 
                size="small" 
                onClick={handleToggleFeatures} 
                fullWidth 
                endIcon={<ExpandMoreIcon sx={{transform: expandedFeatures ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s'}}/>}
                sx={{textTransform:'none', color:'text.secondary', mt:0.5, mb:1, justifyContent:'space-between', px:1}}
            >
                {expandedFeatures ? 'Show Less' : `Show ${displayFeatures.length - featureLimit} More`}
            </Button>
        )}
      </>
    );
  };

  const renderActionButton = () => {
    const commonSx = { 
        py: 1.5, textTransform: 'none', fontWeight: 'bold', fontSize: '1rem', borderRadius: '12px',
        transition: theme.transitions.create(['transform', 'box-shadow']),
        '&:hover': { transform: 'translateY(-2px)' },
        '&.Mui-disabled': { boxShadow: 'none', transform: 'none' }
    };
    
    let buttonProps = {
      fullWidth: true,
      variant: "contained",
      disabled: isLoading || !stripePriceId,
      sx: { ...commonSx }
    };

    if (isLoading) {
        buttonProps.startIcon = <CircularProgress size={22} color="inherit" />;
        buttonProps.children = "Processing...";
    } else {
        buttonProps.endIcon = <ArrowRightIcon />;
    }
    
    if (isCurrentPlan && onManageInPortal) { // Primary action if it's the current plan
      return <Button {...buttonProps} variant="outlined" color="success" startIcon={<ManageIcon />} endIcon={undefined} onClick={onManageInPortal} disabled={isLoading}>Manage Current Plan</Button>;
    }
    if (isDesignatedFreeTier && isEligibleForFreeTier && onActivateFreeTier && stripePriceId) {
      return <Button {...buttonProps} color="success" startIcon={<PremiumIcon />} onClick={() => onActivateFreeTier(stripePriceId)}>Activate Free Tier</Button>;
    }
    // If it's a subscription, not the current one, and user has an active sub (so they'd be switching)
    if (isSubscription && !isCurrentPlan && onManageInPortal && stripePriceId) {
         // This case implies the user HAS an active subscription and is looking at OTHER subscription plans.
         // Stripe's best practice is to manage plan changes (upgrades/downgrades) in the Customer Portal.
      return <Button {...buttonProps} variant="outlined" color="primary" startIcon={<ManageIcon />} endIcon={undefined} onClick={onManageInPortal} disabled={isLoading}>Switch Plan in Portal</Button>;
    }
    if (isOneTimePurchase && stripePriceId && onSubscribe) {
      return <Button {...buttonProps} color="secondary" startIcon={<ShoppingCartIcon />} onClick={() => onSubscribe(stripePriceId, false, quantity)}>Buy {plan.credits_to_add * quantity} Credit{plan.credits_to_add * quantity !== 1 ? 's' : ''}</Button>;
    }
    if (isSubscription && !isCurrentPlan && onSubscribe && stripePriceId) { // Not current, but can subscribe
      return <Button {...buttonProps} color="primary" startIcon={<AutorenewIcon />} onClick={() => onSubscribe(stripePriceId, true, 1)}>Subscribe to {plan?.tier_name || "Plan"}</Button>;
    }
    
    return <Button {...buttonProps} disabled>Unavailable</Button>;
  };

  const cardTitle = plan?.tier_name || (isOneTimePurchase ? `Credit Pack` : "Subscription Plan");
  const cardBorderColor = isCurrentPlan ? theme.palette.success.main : 
                         (isDesignatedFreeTier && isEligibleForFreeTier ? theme.palette.info.main : 
                         alpha(theme.palette.divider, 0.7));
  const cardElevation = isCurrentPlan || (isDesignatedFreeTier && isEligibleForFreeTier) ? 3 : 1;

  if (!plan) {
      return (
          <SectionCard title="Loading Plan..." interactive={false} delay={delay} fullHeight={false} sx={{minHeight: { xs: 'auto', sm: 480 }}}>
              <Skeleton variant="rectangular" height={380} sx={{borderRadius:'16px', bgcolor: alpha(theme.palette.text.primary, 0.03)}}/>
          </SectionCard>
      )
  }
  
  // Corrected logic for helperText using parsed price
  const priceForHelperText = parseFloat(plan?.price_usd_decimal);

  return (
    <SectionCard
      title={cardTitle}
      icon={isSubscription ? <AutorenewIcon/> : (isDesignatedFreeTier ? <PremiumIcon/> : <TagIcon/>)}
      delay={delay}
      elevation={cardElevation}
      interactive
      sx={{
        borderColor: cardBorderColor,
        borderWidth: isCurrentPlan || (isDesignatedFreeTier && isEligibleForFreeTier) ? '2px' : '1px',
        minHeight: { xs: 'auto', sm: 480 },
        position: 'relative',
        background: theme.palette.background.paper,
      }}
      contentSx={{p: {xs: 2, sm: 2.5}, justifyContent: 'space-between'}}
      fullHeight={true}
    >
      <Box flexGrow={1} display="flex" flexDirection="column">
        <Stack direction="row" spacing={1} sx={{ mb: 1.5, minHeight:30 }}>
            {isCurrentPlan && <Chip label="Your Active Plan" color="success" size="small" icon={<CheckCircleIcon />} variant="filled" sx={{fontWeight:500, borderRadius:'8px'}} />}
            {isDesignatedFreeTier && !isCurrentPlan && isEligibleForFreeTier && <Chip label="Free Trial Eligible!" color="info" size="small" icon={<PremiumIcon />} variant="filled" sx={{fontWeight:500, borderRadius:'8px'}} />}
            {plan?.is_popular && <Chip label="Popular Choice" color="warning" size="small" variant="outlined" sx={{fontWeight:500, borderRadius:'8px'}} />}
        </Stack>
        
        {renderPriceSection()}
        {renderCreditsInfo()}
        {renderFeaturesList()}
        
        {isOneTimePurchase && (
          <TextField
            label={`Number of Credits (x${plan.credits_to_add || 1})`} // Clarify quantity is of packs
            type="number"
            value={quantity} // This quantity is for "number of packs"
            onChange={handleQuantityChange}
            fullWidth
            size="small"
            variant="outlined"
            InputProps={{ 
                inputProps: { min: plan?.min_quantity || 1, max: plan?.max_quantity || 10000, step: 1 },
                sx: { borderRadius: '10px' }
            }}
            sx={{ mt: 'auto', mb: 2.5, pt:1 }}
            helperText={
              plan?.min_quantity && plan.min_quantity > 1
                ? `Min. purchase: ${plan.min_quantity} packs.`
                : (typeof priceForHelperText === 'number' && !isNaN(priceForHelperText) ? `$${priceForHelperText.toFixed(2)} per pack of ${plan.credits_to_add || 1} credits` : 'Enter quantity')
            }
          />
        )}
      </Box>
      <Box sx={{pt:1.5}}>
        {renderActionButton()}
      </Box>
    </SectionCard>
  );
};

export default PlanCard;